import { Analytics, AnalyticsInstance } from 'analytics';
// @ts-ignore
import mixpanelPlugin from '@analytics/mixpanel';
import { useConfigStore } from 'Hooks/zustand/useConfigStore';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import { getDeployedRegionFromUrl } from 'Utils';

class Insights {
    private static insights: Insights;
    private static analyticsInstance: AnalyticsInstance;

    constructor() {
        const { appConfig } = useConfigStore.getState();
        const analyticsToken = appConfig?.analytics?.mixpanel.token ?? '';

        Insights.analyticsInstance = Insights.initAnalytics(analyticsToken);
    }

    private static initAnalytics(token: string) {
        const plugins = [
            mixpanelPlugin({
                token
            })
        ];

        return Analytics({
            app: 'rules-management',
            plugins
        });
    }

    public static getAnalytics(): AnalyticsInstance {
        if (!Insights.insights) {
            Insights.insights = new Insights();
        }

        return Insights.analyticsInstance;
    }
}

export enum EventTypes {
    page,
    track,
    identify
}

export enum EventNames {
    pageView = 'Page View',
    click = 'Click',
    trackUser = 'trackUser'
}

type TrackEvent = {
    eventType: EventTypes;
    eventName: EventNames;
    payload?: TrackPayload;
};

export const trackEvent = ({ eventType, eventName, payload }: TrackEvent) =>
    new Promise<void>((resolve, reject) => {
        try {
            const analyticsInstance = Insights.getAnalytics();
            const { getUserInfo } = useAccessTokenStore.getState();
            const { customerId, tenantKey } = getUserInfo();

            const defaultPayload: TrackPayload = {
                customerId,
                environment: process.env.NODE_ENV,
                tenantKey,
                version: process.env.MASTERCARD_PACKAGE_VERSION,
                deployedRegion: getDeployedRegionFromUrl(window?.location?.href ?? undefined)
            };

            switch (eventType) {
                case EventTypes.page: {
                    analyticsInstance.page({
                        ...defaultPayload,
                        ...payload
                    });
                    break;
                }
                case EventTypes.track: {
                    analyticsInstance.track(eventName, {
                        ...defaultPayload,
                        ...payload
                    });
                    break;
                }
                case EventTypes.identify: {
                    analyticsInstance.identify(payload?.userId || '');
                    break;
                }
                default: {
                    throw new Error(`Event type: ${eventType} not implemented`);
                }
            }

            /**
             * Sidekick.js needs to be loaded before window property connect is available. {@link ./src/index.html}
             *
             * Click event tracking can be added by attaching a `data-track-click` attribute to any clickable element:
             * <a href="/path" data-track-click="Clickable Element">My clickable element</a>
             *
             * @note Analytics provided by Mastercard is ONLY enabled in production.
             * @url https://asset.connect.mastercard.com/community/sidekick.v1.js
             * */
            if (typeof window.connect !== 'undefined') {
                /** Check if analytics is enabled */
                if (window.connect.analytics.getIsEnabled()) {
                    window.connect.analytics.track(eventName, {
                        ...defaultPayload,
                        ...payload
                    });
                }
            }

            resolve();
        } catch (e) {
            reject(e);
        }
    });
